import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function usePagination(data, resetKey) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const total = data.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [pageSize, resetKey]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const start = (page - 1) * pageSize;
  return {
    page, setPage, pageSize, setPageSize, pageCount, total,
    pageItems: data.slice(start, start + pageSize),
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
  };
}

export function PaginationBar({ pg, testid = "pagination" }) {
  if (!pg.total) return null;
  const { page, pageCount } = pg;
  const nums = [];
  [1, page - 1, page, page + 1, pageCount].forEach(n => {
    if (n >= 1 && n <= pageCount && !nums.includes(n)) nums.push(n);
  });
  nums.sort((a, b) => a - b);
  const steps = [];
  nums.forEach((n, i) => {
    if (i && n - nums[i - 1] > 1) steps.push("…");
    steps.push(n);
  });
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap border-t border-border px-3 py-2" data-testid={testid}>
      <div className="text-[11px] text-muted-foreground font-mono" data-testid={`${testid}-range`}>
        {pg.from} to {pg.to} of {pg.total} records
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" data-testid={`${testid}-prev`} disabled={page <= 1}
          onClick={() => pg.setPage(page - 1)} className="h-7 rounded-sm text-[10px] uppercase tracking-wider">
          <ChevronLeft className="w-3 h-3 mr-0.5"/>Prev
        </Button>
        {steps.map((n, i) => n === "…"
          ? <span key={`d${i}`} className="text-muted-foreground text-xs px-1">…</span>
          : <Button key={n} size="sm" variant={n === page ? "default" : "outline"} data-testid={`${testid}-page-${n}`}
              onClick={() => pg.setPage(n)}
              className={`h-7 w-7 p-0 rounded-sm text-[11px] ${n === page ? "bg-primary text-primary-foreground" : ""}`}>{n}</Button>)}
        <Button size="sm" variant="outline" data-testid={`${testid}-next`} disabled={page >= pageCount}
          onClick={() => pg.setPage(page + 1)} className="h-7 rounded-sm text-[10px] uppercase tracking-wider">
          Next<ChevronRight className="w-3 h-3 ml-0.5"/>
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Per page</span>
        <Select value={String(pg.pageSize)} onValueChange={(v) => pg.setPageSize(Number(v))}>
          <SelectTrigger data-testid={`${testid}-size`} className="h-7 w-20 rounded-sm bg-secondary border-border text-xs"><SelectValue/></SelectTrigger>
          <SelectContent>
            {[25, 50, 100, 200].map(n => <SelectItem key={n} value={String(n)} data-testid={`${testid}-size-${n}`}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
